/**
 * Interpolation utilities for visualization
 * Pure functions for calculating grid values and z-axis extents
 */

import * as d3 from 'd3';

export interface MeasurementPoint {
  x: number;
  y: number;
  _value: number;
  [key: string]: any;
}

/**
 * Calculate default z-axis extent (min/max) from measurement points
 * Adds 10% buffer to accommodate visualization range
 *
 * @param points - Array of measurement points with _value property
 * @returns Tuple of [minValue, maxValue] with buffer applied
 */
export function calculateDefaultZExtent(points: MeasurementPoint[]): [number, number] {
  if (!points || points.length === 0) {
    return [1, 12]; // Default range if no data
  }

  const values = points
    .map((point) => point._value)
    .filter((val) => val !== undefined && !isNaN(val));

  if (values.length === 0) {
    return [1, 12]; // Default range if no valid values
  }

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const buffer = (maxVal - minVal) * 0.1; // 10% buffer

  return [minVal - buffer, maxVal + buffer];
}

/**
 * Calculate interpolated grid values using Inverse Distance Weighting (IDW)
 * Creates a regular grid for contour plot generation
 *
 * Algorithm:
 * - For each grid cell, find nearby measurement points (within 16m radius)
 * - Weight each point by inverse cube of distance (1/d³)
 * - Calculate weighted average of measurement values
 *
 * @param points - Array of measurement points with x, y, _value properties
 * @param gridResolution - Number of grid cells in each dimension
 * @param xScale - D3 scale for x-axis
 * @param yScale - D3 scale for y-axis
 * @returns Flat array of interpolated values (length: (gridResolution+1)²)
 */
export function calculateGrid(
  points: MeasurementPoint[],
  gridResolution: number,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): number[] {
  const xExtent = xScale.domain();
  const yExtent = yScale.domain();
  const gridValues: number[] = [];

  const xStep = (xExtent[1] - xExtent[0]) / gridResolution;
  const yStep = (yExtent[1] - yExtent[0]) / gridResolution;

  // Iterate through grid cells (row by row)
  for (let j = 0; j <= gridResolution; j++) {
    for (let i = 0; i <= gridResolution; i++) {
      const x = xExtent[0] + xStep * i;
      const y = yExtent[0] + yStep * j;

      // Calculate weighted measurements using Inverse Distance Weighting
      const weightedMeasurements = points
        // First filter out invalid points
        .filter((d) => {
          return (
            d.x !== undefined &&
            d.y !== undefined &&
            d._value !== undefined &&
            !isNaN(d.x) &&
            !isNaN(d.y) &&
            !isNaN(d._value)
          );
        })
        .map((d) => {
          const dist = Math.hypot(d.x - x, d.y - y);
          return {
            dist,
            value: d._value,
            // IDW with power of 3 (1/d³) - closer points have much more influence
            weight: Math.pow(dist, -3.0),
          };
        })
        .filter((d) => d.dist < 16); // Only consider points within 16m radius

      // Calculate weighted average
      const sumWeights = d3.sum(weightedMeasurements.map((d) => d.weight));
      const value = d3.sum(weightedMeasurements.map((d) => d.value * d.weight)) / sumWeights;

      gridValues.push(value || 0); // Use 0 if no nearby points
    }
  }

  return gridValues;
}

/**
 * Validate z-axis extent values
 * Ensures min < max and values are within reasonable bounds
 *
 * @param zExtent - Tuple of [min, max] values
 * @returns True if extent is valid, false otherwise
 */
export function isValidZExtent(zExtent: [number, number]): boolean {
  const [min, max] = zExtent;
  return !isNaN(min) && !isNaN(max) && isFinite(min) && isFinite(max) && min < max;
}

/**
 * Clamp a value to stay within z-axis extent
 * Useful for legend indicators and scale normalization
 *
 * @param value - Value to clamp
 * @param zExtent - Tuple of [min, max] bounds
 * @returns Clamped value within [min, max] range
 */
export function clampToZExtent(value: number, zExtent: [number, number]): number {
  const [min, max] = zExtent;
  return Math.max(min, Math.min(max, value));
}
